import { jsPDF } from "jspdf";

// shipping_address is the backend's already-parsed copy of Walmart's
// per-line shippingInfo.postalAddress (see _flatten_order_lines). Some
// orders end up with that field empty even though the ORIGINAL Walmart
// payload still has it — raw_line is only present when the caller used
// getOrderDetail's select('*') (the OrderDetailModal path), not the
// lighter-weight list endpoint, so this fallback only helps when a full
// line object is available. If a line has neither, street/city just
// aren't recoverable and the slip will render with whatever's left
// (name + state/ZIP from the order row).
const parseRawLinePostalAddress = (line) => {
    const raw = line?.raw_line;
    if (!raw) return null;
    // Walmart nests this either directly on the orderLine or under
    // shippingInfo depending on API version — check both shapes.
    const postal = raw?.shippingInfo?.postalAddress || raw?.postalAddress || null;
    if (!postal) return null;
    return {
        name: postal.name || "",
        address1: postal.address1 || postal.addressLineOne || "",
        address2: postal.address2 || postal.addressLineTwo || "",
        city: postal.city || "",
        state: postal.state || "",
        postalCode: postal.postalCode || "",
    };
};

const deriveShipTo = (order, lines) => {
    const line0 = lines?.[0] || {};
    const structured = line0.shipping_address || null;
    const fallback = (!structured || !structured.address1) ? parseRawLinePostalAddress(line0) : null;
    const postal = structured || fallback;
    return {
        name: postal?.name || order?.buyer_name || "",
        addressLine1: postal?.address1 || postal?.addressLine1 || "",
        addressLine2: postal?.address2 || postal?.addressLine2 || "",
        city: postal?.city || "",
        state: postal?.state || order?.state || "",
        postalCode: postal?.postalCode || order?.postal_code || "",
    };
};

const loadFromInfo = () => {
    try {
        const raw = localStorage.getItem("sww_ship_from_address");
        const saved = raw ? JSON.parse(raw) : null;
        return {
            company: saved?.companyName || saved?.contactName || "Your Store",
            phone: saved?.phone || "",
        };
    } catch {
        return { company: "Your Store", phone: "" };
    }
};

// Manual word-wrap that lets ONE specific word render as a colored,
// underlined, clickable link while the rest of the paragraph stays plain
// black text — jsPDF's built-in splitTextToSize has no concept of mixed
// styling within a wrapped paragraph, so this does it word-by-word.
const renderParagraphWithLink = (doc, text, linkWord, url, x, y, maxWidth, lineHeight) => {
    const words = text.split(" ");
    const spaceWidth = doc.getTextWidth(" ");
    let cursorX = x;
    let cursorY = y;

    words.forEach((word) => {
        const isLink = word === linkWord;
        const wordWidth = doc.getTextWidth(word);
        if (cursorX + wordWidth > x + maxWidth) {
            cursorX = x;
            cursorY += lineHeight;
        }
        if (isLink) {
            doc.setTextColor(0, 113, 206);
            doc.textWithLink(word, cursorX, cursorY, { url });
            doc.setLineWidth(0.6);
            doc.setDrawColor(0, 113, 206);
            doc.line(cursorX, cursorY + 1.5, cursorX + wordWidth, cursorY + 1.5);
            doc.setTextColor(0, 0, 0);
        } else {
            doc.text(word, cursorX, cursorY);
        }
        cursorX += wordWidth + spaceWidth;
    });

    return cursorY + lineHeight;
};

export const generatePackingSlipPdf = (order, lines) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = 612, pageH = 792;
    const marginX = 45;
    const rightEdge = pageW - marginX;
    const contentWidth = rightEdge - marginX;

    const shipTo = deriveShipTo(order, lines);
    const { company, phone } = loadFromInfo();

    // --- Outer page border ---
    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(1.2);
    doc.rect(28, 28, pageW - 56, pageH - 56);

    let y = 68;

    // --- Header: company name (left) / Walmart Marketplace wordmark (right) ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(19);
    doc.setTextColor(20, 20, 20);
    doc.text(company, marginX, y);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(19);
    doc.setTextColor(0, 113, 206);
    doc.text("Walmart", rightEdge, y - 3, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(72, 165, 224);
    doc.text("Marketplace", rightEdge, y + 12, { align: "right" });
    doc.setTextColor(0, 0, 0);

    y += 28;
    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(1);
    doc.line(marginX, y, rightEdge, y);
    y += 24;

    // --- Order # / Order Date row ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const orderNum = order.customer_order_id || order.purchase_order_id;
    const orderDate = order.order_date ? new Date(order.order_date).toISOString().slice(0, 10) : "";
    doc.text("Order #: ", marginX, y);
    const orderLabelWidth = doc.getTextWidth("Order #: ");
    doc.setFont("helvetica", "normal");
    doc.text(String(orderNum), marginX + orderLabelWidth, y);

    doc.setFont("helvetica", "bold");
    const dateLabel = "Order Date: ";
    const dateValueWidth = doc.getTextWidth(orderDate);
    const dateLabelWidth = doc.getTextWidth(dateLabel);
    doc.text(dateLabel, rightEdge - dateValueWidth - dateLabelWidth, y);
    doc.setFont("helvetica", "normal");
    doc.text(orderDate, rightEdge - dateValueWidth, y);

    y += 14;
    doc.setLineWidth(0.75);
    doc.line(marginX, y, rightEdge, y);
    y += 26;

    // --- From / Ship to / Bill to (three columns) ---
    const colWidth = contentWidth / 3;
    const col1X = marginX, col2X = marginX + colWidth, col3X = marginX + colWidth * 2;
    const addrLineHeight = 13;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text("From", col1X, y);
    doc.text("Ship to", col2X, y);
    doc.text("Bill to", col3X, y);

    const fromLines = [company, phone].filter(Boolean);
    const custLines = [
        shipTo.name,
        shipTo.addressLine1,
        shipTo.addressLine2,
        [shipTo.city, shipTo.state, shipTo.postalCode].filter(Boolean).join(", "),
    ].filter(Boolean);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const addrY = y + 15;
    fromLines.forEach((line, i) => doc.text(line, col1X, addrY + i * addrLineHeight));
    custLines.forEach((line, i) => doc.text(line, col2X, addrY + i * addrLineHeight));
    custLines.forEach((line, i) => doc.text(line, col3X, addrY + i * addrLineHeight));

    const tallestBlock = Math.max(fromLines.length, custLines.length);
    y = addrY + tallestBlock * addrLineHeight + 24;

    // --- Item table ---
    const tableX = marginX, tableW = contentWidth;
    const headerH = 22;
    doc.setFillColor(150, 175, 182);
    doc.rect(tableX, y, tableW, headerH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    doc.text("QTY", tableX + 8, y + 14);
    doc.text("Item Number", tableX + 55, y + 14);
    doc.text("Description", tableX + 195, y + 14);
    y += headerH;

    doc.setFont("helvetica", "normal");
    (lines || []).forEach((l) => {
        const descMaxWidth = tableW - 195 - 10;
        const desc = doc.splitTextToSize(l.product_name || "", descMaxWidth);
        const rowH = Math.max(30, desc.length * 13 + 14);

        doc.setFillColor(242, 242, 242);
        doc.rect(tableX, y, tableW, rowH, "F");

        doc.setTextColor(20, 20, 20);
        doc.text(String(l.quantity ?? ""), tableX + 8, y + 18);
        doc.text(String(l.item_id || l.sku || ""), tableX + 55, y + 18);
        doc.text(desc, tableX + 195, y + 18);

        y += rowH;
    });

    y += 26;

    // --- Thank-you lines ---
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Thanks for purchasing from ${company} on Walmart.com.`, marginX, y);
    y += 15;
    doc.text("Since your order is from a Walmart Marketplace Seller, here are a few important things to know:", marginX, y);
    y += 22;

    // --- Red-bordered callout box with the bullet + hyperlink ---
    const boxX = marginX, boxY = y;
    const boxW = contentWidth;
    const bulletTextX = boxX + 22;
    const bulletMaxWidth = boxW - 34;

    const contactText = "To email this seller or start a return, go to https://www.walmart.com/orders or click on My Account > Order Number > See Details > Contact this seller.";
    // Pre-measure the wrapped height by rendering to a throwaway y first
    // isn't possible without drawing, so render once at a temp y far off
    // page to count lines, then draw the real box using that height.
    const lineHeight = 13;
    let probeY = 0;
    const words = contactText.split(" ");
    let lineCount = 1;
    let cursorW = 0;
    const spaceW = doc.getTextWidth(" ");
    words.forEach((w) => {
        const ww = doc.getTextWidth(w);
        if (cursorW + ww > bulletMaxWidth) { lineCount++; cursorW = 0; }
        cursorW += ww + spaceW;
    });
    const boxH = 20 + lineCount * lineHeight;

    doc.setDrawColor(200, 30, 30);
    doc.setLineWidth(1.2);
    doc.rect(boxX, boxY, boxW, boxH);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("•", boxX + 10, boxY + 18);
    renderParagraphWithLink(
        doc, contactText, "https://www.walmart.com/orders", "https://www.walmart.com/orders",
        bulletTextX, boxY + 18, bulletMaxWidth, lineHeight
    );

    y = boxY + boxH + 22;

    // --- Footer ---
    doc.setFontSize(10);
    doc.text("Need help with your order?", marginX, y);
    y += 15;
    doc.text("Go to Walmart.com/account.", marginX, y);

    return doc;
};

export const downloadPackingSlip = (order, lines) => {
    const doc = generatePackingSlipPdf(order, lines);
    window.open(doc.output("bloburl"), "_blank");
};